import { useEffect, useRef, useState } from "react";
import TomSelect from "tom-select";
import Swal from "sweetalert2";

const SakitForm = ({ isModalOpen, onClose }) => {
    const [keterangan, setKeterangan] = useState("");
    const [jumlahHari, setJumlahHari] = useState(1);
    const [tanggalSakit, setTanggalSakit] = useState([""]);
    const [tanggalMasuk, setTanggalMasuk] = useState("");
    const [suratDokter, setSuratDokter] = useState(null); // File object
    const [previewUrl, setPreviewUrl] = useState(null);   // Preview URL gambar

    // Canvas & TomSelect
    const canvasRef = useRef(null);
    const isDrawing = useRef(false);
    const ctxRef = useRef(null);
    const tomSelectRef = useRef(null);
    const tomInstance = useRef(null);

    const styles = {
        field: { marginBottom: 16 },
        label: { display: "block", marginBottom: 8, fontWeight: 500 },
        input: {
            width: "100%",
            padding: "12px",
            borderRadius: 12,
            border: "1.5px solid #ddd",
            fontSize: 15,
            backgroundColor: "#fff",
            outline: "none",
            boxSizing: "border-box",
        },
    };

    // ── Setup canvas saat modal dibuka ──
    useEffect(() => {
        if (!isModalOpen || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.lineCap = "round";
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 3;
        ctxRef.current = ctx;
        const resize = () => {
            const { width } = canvas.getBoundingClientRect();
            canvas.width = width * window.devicePixelRatio;
            canvas.height = 150 * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener("resize", resize);

        // Setup TomSelect jika belum (dan list karyawan ada)
        if (!tomInstance.current && tomSelectRef.current) {
            const dataKaryawan = loadKaryawan();
            tomInstance.current = new TomSelect(tomSelectRef.current, {
                valueField: "id",
                labelField: "nama",
                searchField: "nama",
                placeholder: "Pilih karyawan pengganti",
                options: dataKaryawan,
                render: {
                    option: (data, escape) => `
                        <div style="padding:6px 8px">
                            <strong>${escape(data.nama)}</strong>
                        </div>
                    `,
                    item: (data, escape) => `
                        <div>${escape(data.nama)}</div>
                    `,
                },
            });
        }

        return () => {
            window.removeEventListener("resize", resize);
            tomInstance.current?.destroy();
            tomInstance.current = null;
        };
    }, [isModalOpen]);

    // Reset canvas saat modal buka ulang
    useEffect(() => {
        if (isModalOpen && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            ctx?.clearRect(0, 0, canvas.width, canvas.height);
        }
        if (!isModalOpen) {
            setKeterangan("");
            setJumlahHari(1);
            setTanggalSakit([""]);
            setTanggalMasuk("");
            setSuratDokter(null);
            setPreviewUrl(null);
            if (tomInstance.current) {
                tomInstance.current.clear();
            }
        }
    }, [isModalOpen]);

    // ── Drawing handlers ──
    const getPos = (e, isTouch) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: (isTouch ? e.touches[0].clientX : e.clientX) - rect.left,
            y: (isTouch ? e.touches[0].clientY : e.clientY) - rect.top,
        };
    };
    const startDrawing = (e, isTouch = false) => {
        isDrawing.current = true;
        const { x, y } = getPos(e, isTouch);
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
    };
    const draw = (e, isTouch = false) => {
        if (!isDrawing.current) return;
        const { x, y } = getPos(e, isTouch);
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
    };
    const stopDrawing = () => { isDrawing.current = false; };
    const clearSignature = () => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    // ── Jumlah hari handler ──
    const handleJumlahHariChange = (e) => {
        const val = e.target.value;
        setJumlahHari(val);
        const n = parseInt(val);
        if (!val || isNaN(n) || n <= 0) { setTanggalSakit([]); return; }
        setTanggalSakit(prev => {
            if (prev.length < n) return [...prev, ...Array(n - prev.length).fill("")];
            return prev.slice(0, n);
        });
    };

    // ── Upload surat dokter ──
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setSuratDokter(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleCamera = () => {
        document.getElementById("surat-dokter-input").click();
    };

    // ── Helper ──
    const loadKaryawan = () => {
        try {
            const raw = localStorage.getItem("karyawan_pengganti_list");
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            console.log("Gagal parse karyawan:", err);
            return [];
        }
    };

    const getKaryawanId = () => {
        try { return JSON.parse(localStorage.getItem("data_karyawan"))?.id || null; }
        catch { return null; }
    };

    const getPeriod = () => {
        const now = new Date();
        let month = now.getMonth(), year = now.getFullYear();
        if (now.getDate() >= 21) month++;
        if (month > 11) { month = 0; year++; }
        return `${new Date(year, month).toLocaleString("en-US", { month: "long" })} ${year}`;
    };

    // ── Submit ──
    const handleSubmit = async () => {
        const karyawanId = getKaryawanId();
        if (!karyawanId) {
            await Swal.fire({ icon: "error", title: "Data Tidak Ditemukan", text: "Silakan login ulang." });
            return;
        }
        if (!keterangan.trim()) {
            await Swal.fire({ icon: "warning", title: "Keterangan Kosong", text: "Keterangan wajib diisi." });
            return;
        }
        if (tanggalSakit.some(t => !t)) {
            await Swal.fire({ icon: "warning", title: "Tanggal Belum Lengkap", text: "Semua tanggal sakit wajib diisi." });
            return;
        }
        if (!tanggalMasuk) {
            await Swal.fire({ icon: "warning", title: "Tanggal Masuk Kosong", text: "Tanggal masuk kembali wajib diisi." });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Ajukan Izin Sakit?",
            text: "Pastikan data yang diisi sudah benar",
            showCancelButton: true,
            confirmButtonText: "Ya, Ajukan",
            cancelButtonText: "Batal",
        });
        if (!confirm.isConfirmed) return;

        const penggantiId = tomInstance.current?.getValue() || "";

        const formData = new FormData();
        formData.append("karyawan_id", karyawanId);
        formData.append("absen_id", "6"); // sesuaikan ID jenis sakit dari API
        formData.append("periode", getPeriod());
        formData.append("tanggal", tanggalSakit[0]);
        formData.append("jumlah_hari", jumlahHari);
        formData.append("tanggal_ijin", tanggalSakit.join(", "));
        formData.append("tanggal_masuk", tanggalMasuk);
        formData.append("masuk", penggantiId);
        formData.append("nama", keterangan);
        formData.append("telat", "1");
        formData.append("bolos", "0");

        if (canvasRef.current) {
            formData.append("ttd_pemohon", canvasRef.current.toDataURL("image/png"));
        }
        if (suratDokter) {
            formData.append("surat_dokter", suratDokter);
        }

        // Tampilkan success langsung (Background processing)
        Swal.fire({ icon: "success", title: "Berhasil!", text: "Pengajuan izin sakit sedang diproses...", timer: 1500, showConfirmButton: false });
        onClose?.();

        // Proses API di background
        fetch("https://jne-cilacap.com/slip/public/api/absen", { method: "POST", body: formData })
            .then(async (res) => {
                if (!res.ok) throw new Error(await res.text());
                console.log("Submit sakit sukses (background)");
            })
            .catch(err => {
                console.error("Gagal mengirim sakit (background):", err.message);
                Swal.fire({ icon: "error", title: "Gagal Mengirim", text: "Proses izin sakit di latar belakang gagal: " + err.message });
            });
    };

    return (
        <div style={{ width: "100%", boxSizing: "border-box" }}>

            {/* KETERANGAN */}
            <div style={styles.field}>
                <label style={styles.label}>Keterangan Sakit:</label>
                <textarea
                    value={keterangan}
                    onChange={e => setKeterangan(e.target.value)}
                    placeholder="Contoh: Demam tinggi disertai batuk, istirahat total"
                    rows={4}
                    style={{ ...styles.input, resize: "none" }}
                />
            </div>

            {/* JUMLAH HARI */}
            <div style={styles.field}>
                <label style={styles.label}>Jumlah Hari:</label>
                <input
                    type="number"
                    min="1"
                    value={jumlahHari}
                    onChange={handleJumlahHariChange}
                    placeholder="Masukkan jumlah hari sakit"
                    style={styles.input}
                />
            </div>

            {/* TANGGAL DINAMIS */}
            {jumlahHari > 0 && Array.from({ length: Math.ceil(jumlahHari) }).map((_, i) => (
                <div key={i} style={styles.field}>
                    <label style={styles.label}>Tanggal Sakit Hari ke-{i + 1}:</label>
                    <input
                        type="date"
                        value={tanggalSakit[i] || ""}
                        onChange={e => {
                            const arr = [...tanggalSakit];
                            arr[i] = e.target.value;
                            setTanggalSakit(arr);
                        }}
                        style={styles.input}
                    />
                </div>
            ))}

            {/* TANGGAL MASUK KEMBALI */}
            {jumlahHari > 0 && (
                <div style={styles.field}>
                    <label style={styles.label}>Tanggal Masuk Kembali:</label>
                    <input
                        type="date"
                        value={tanggalMasuk}
                        onChange={e => setTanggalMasuk(e.target.value)}
                        style={styles.input}
                    />
                </div>
            )}

            {/* PENGGANTI */}
            <div style={styles.field}>
                <label style={styles.label}>Tugas Digantikan Oleh:</label>
                <select ref={tomSelectRef} style={{ width: "100%" }}>
                    <option value="">Pilih karyawan pengganti</option>
                </select>
            </div>

            {/* UPLOAD SURAT DOKTER */}
            <div style={styles.field}>
                <label style={styles.label}>Surat Keterangan Dokter:</label>

                {/* Hidden file input: accept image + pdf, bisa kamera */}
                <input
                    id="surat-dokter-input"
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                />

                {/* Area upload */}
                <div
                    onClick={handleCamera}
                    style={{
                        border: "2px dashed #93c5fd",
                        borderRadius: 12,
                        padding: "20px 16px",
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: "#eff6ff",
                        transition: "background .2s",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#dbeafe")}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#eff6ff")}
                >
                    {previewUrl ? (
                        previewUrl.startsWith("blob:") && suratDokter?.type === "application/pdf" ? (
                            <p style={{ margin: 0, color: "#2563eb", fontWeight: 500 }}>📄 {suratDokter.name}</p>
                        ) : (
                            <img
                                src={previewUrl}
                                alt="Preview surat"
                                style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, objectFit: "contain" }}
                            />
                        )
                    ) : (
                        <>
                            <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
                            <p style={{ margin: 0, color: "#2563eb", fontWeight: 500, fontSize: 14 }}>
                                Foto / Upload Surat Dokter
                            </p>
                            <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 12 }}>
                                Klik untuk ambil foto atau pilih file (JPG, PNG, PDF)
                            </p>
                        </>
                    )}
                </div>

                {previewUrl && (
                    <button
                        onClick={() => { setSuratDokter(null); setPreviewUrl(null); }}
                        style={{ marginTop: 8, color: "#dc2626", background: "none", border: "none", fontSize: 13, cursor: "pointer" }}
                    >
                        Hapus File
                    </button>
                )}
            </div>

            {/* TANDA TANGAN */}
            <div style={styles.field}>
                <label style={styles.label}>Tanda Tangan:</label>
                <canvas
                    ref={canvasRef}
                    width={300}
                    height={150}
                    style={{
                        border: "1.5px dashed #ccc",
                        borderRadius: 12,
                        width: "100%",
                        height: 150,
                        backgroundColor: "#f9f9f9",
                        touchAction: "none",
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={e => { e.preventDefault(); startDrawing(e, true); }}
                    onTouchMove={e => { e.preventDefault(); draw(e, true); }}
                    onTouchEnd={stopDrawing}
                />
                <button
                    onClick={clearSignature}
                    style={{ marginTop: 8, color: "#dc2626", background: "none", border: "none", fontSize: 13, cursor: "pointer" }}
                >
                    Hapus Tanda Tangan
                </button>
            </div>

            {/* TOMBOL SUBMIT */}
            <button
                onClick={handleSubmit}
                style={{
                    width: "100%",
                    padding: "14px",
                    backgroundColor: "#2563eb",
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 16,
                    cursor: "pointer",
                    marginTop: 4,
                }}
            >
                Kirim Pengajuan Sakit
            </button>
        </div>
    );
};

export default SakitForm;
